/**
 * Centralized form submission helper.
 *
 * Every form submission across the site is routed through here so it is:
 *   1. Recorded in the Famous CRM via the hosted /api/crm subscribe endpoint
 *   2. Forwarded to the journey@zeratravel.com inbox (via the CRM's
 *      notification routing — the email is included in the payload as
 *      `notify_email` and `forward_to` so the CRM picks it up).
 *
 * All callers are non-blocking: failures are swallowed and only logged so
 * the user-facing UX (e.g. "Thank you" screens) is never disrupted by
 * an outage of the CRM endpoint.
 */

const CRM_SUBSCRIBE_URL = '/api/crm/69cbf6b38dc4f4af5d55476d/subscribe';

export const NOTIFY_EMAIL = 'journey@zeratravel.com';

export type NotifyFormSource =
  | 'newsletter'
  | 'plan-journey'
  | 'booking'
  | 'corporate-inquiry'
  | 'contact';

export interface NotifyFormPayload {
  /** Required: the email address of the person submitting the form */
  email: string;
  /** Optional first name (improves CRM contact record) */
  firstName?: string;
  /** Optional last name */
  lastName?: string;
  /** Optional phone number */
  phone?: string;
  /** Where on the site the submission came from */
  source: NotifyFormSource;
  /** Human-readable subject line for the email notification */
  subject?: string;
  /** Any structured form data — included in the CRM record + forwarded email */
  details?: Record<string, unknown>;
}

const buildEmailBody = (payload: NotifyFormPayload): string => {
  const lines: string[] = [];
  lines.push(`New ${payload.source} submission from zera.travel`);
  lines.push('');
  if (payload.firstName || payload.lastName) {
    lines.push(`Name: ${[payload.firstName, payload.lastName].filter(Boolean).join(' ')}`);
  }
  lines.push(`Email: ${payload.email}`);
  if (payload.phone) lines.push(`Phone: ${payload.phone}`);
  lines.push('');
  if (payload.details) {
    lines.push('Details:');
    for (const [key, value] of Object.entries(payload.details)) {
      if (value === undefined || value === null || value === '') continue;
      const formatted = Array.isArray(value) ? value.join(', ') : String(value);
      lines.push(`  • ${key}: ${formatted}`);
    }
  }
  return lines.join('\n');
};

/**
 * Submit a form to the CRM and trigger an email to journey@zeratravel.com.
 * Always resolves — never throws — so callers don't need to wrap in try/catch.
 */
export async function notifyForm(payload: NotifyFormPayload): Promise<{ ok: boolean }> {
  try {
    const body = {
      email: payload.email,
      first_name: payload.firstName,
      last_name: payload.lastName,
      phone: payload.phone,
      source: payload.source,
      // The CRM forwards a copy of every submission to this address.
      notify_email: NOTIFY_EMAIL,
      forward_to: NOTIFY_EMAIL,
      subject: payload.subject || `Zera Travel — ${payload.source}`,
      message: buildEmailBody(payload),
      metadata: {
        source: payload.source,
        submitted_at: new Date().toISOString(),
        ...(payload.details || {}),
      },
    };

    const res = await fetch(CRM_SUBSCRIBE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    return { ok: res.ok };
  } catch (err) {
    // Never break the UX — just log.
    // eslint-disable-next-line no-console
    console.error('notifyForm failed', err);
    return { ok: false };
  }
}
